import { PremiumFeatureBadge } from '@src/components/PremiumBadge/PremiumFeatureBadge';
import type { PremiumDrawUsage } from '@lub0v/santa-utils';
import { ArrowRightIcon } from '@src/components/icons/ArrowRightIcon';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { CrownIcon } from '@src/components/icons/CrownIcon';
import { usePremiumModal } from '@src/components/PremiumUpsell/PremiumModal';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { CardInfo } from '@lub0v/santa-utils';
import { Button, ParticipantFace, Select, Stack, Typography, showToast } from '@src/components';
import { RefreshIcon } from '@src/components/icons/RefreshIcon';
import { ThemeArtwork } from '@src/theme/Artwork';
import { LockIcon } from '@src/components/icons/LockIcon';
import { PremiumUpsell } from '@src/components/PremiumUpsell/PremiumUpsell';
import { previewBoxDraw, changePreviewGiftee, insertPreviewParticipant, type DrawAssignment } from './draw';
import { completeManualDraw } from './manualDraw';
import './DrawPreview.css';

export function DrawReadyTitle() {
  const { t } = useTranslation();
  return <span className="draw-preview__title">
    <span className="draw-preview__celebration" aria-hidden="true"><ThemeArtwork role="gift" className="draw-preview__gift" /></span>
    <span>{t('game.draw.previewTitle')}</span>
  </span>;
}

export function DrawPreview({
  cards,
  circleDraw,
  initial,
  onChange,
  disabled,
  premium,
  existingDraw = false,
  onIncompleteChange,
  onSaveSummaryChange,
  freeRepair = false,
  additionalCount = 0,
  onAdditionalPrepare,
  onRepairChange,
  onPremiumUsage,
}: {
  onPremiumUsage?: (usage: PremiumDrawUsage) => void;
  freeRepair?: boolean;
  additionalCount?: number;
  onAdditionalPrepare?: () => void;
  onRepairChange?: (pair: DrawAssignment) => void;
  cards: CardInfo[];
  circleDraw: boolean;
  initial: DrawAssignment[];
  onChange: (assignments: DrawAssignment[]) => void;
  disabled: boolean;
  premium: boolean;
  existingDraw?: boolean;
  onIncompleteChange?: (incomplete: boolean) => void;
  onSaveSummaryChange?: (summary: string) => void;
}) {
  const { t } = useTranslation();
  const openPremium = usePremiumModal();
  const canView = premium || existingDraw;
  const [additionalReady, setAdditionalReady] = useState(false);
  const [assignments, setAssignments] = useState(initial);
  const [originalRecipients] = useState(() => new Map(initial.map(pair => [pair.id, pair.wardId])));
  const [revealed, setRevealed] = useState<Set<number>>(() => new Set((freeRepair || (premium && initial.filter(pair => !pair.wardId).length === 1)) && existingDraw ? initial.filter(pair => !pair.wardId).map(pair => pair.id) : []));
  const [singleRepairActive, setSingleRepairActive] = useState(() => existingDraw && initial.filter(pair => !pair.wardId).length === 1);
  const [clearingSelections, setClearingSelections] = useState(false);
  const [manual, setManual] = useState(() => existingDraw && initial.some(pair => !pair.wardId));
  const [shuffleVersion, setShuffleVersion] = useState(0);
  const [animatingShuffle, setAnimatingShuffle] = useState(false);
  useEffect(() => {
    if (!shuffleVersion) return;
    const timer = setTimeout(() => setAnimatingShuffle(false), 550);
    return () => clearTimeout(timer);
  }, [shuffleVersion]);
  const [selecting, setSelecting] = useState<number | null>(null);
  const [completion, setCompletion] = useState(initial);
  function manualChoice(giver: number, recipient: number) {
    const partial = assignments.map(pair => pair.id === giver ? { ...pair, wardId: recipient } : pair);
    const adjusted = changePreviewGiftee(cards, completion, giver, recipient, circleDraw);
    if (adjusted && partial.every(pair => !pair.wardId || adjusted.some(item => item.id === pair.id && item.wardId === pair.wardId))) return adjusted;
    return completeManualDraw(cards, partial, circleDraw);
  }
  const incomplete = premium && assignments.some(pair => !pair.wardId);
  useEffect(() => { onIncompleteChange?.(incomplete); }, [incomplete, onIncompleteChange]);
  const changedCount = assignments.filter(pair => pair.wardId > 0 && originalRecipients.get(pair.id) !== pair.wardId).length;
  const notificationCount = (additionalReady ? additionalCount : 0) || (existingDraw ? changedCount : cards.length);
  const saveSummary = incomplete && !freeRepair ? t('game.draw.editExisting.notificationCountIncomplete') : notificationCount === 0 ? t('game.draw.editExisting.notificationCountZero') : t('game.draw.editExisting.notificationCount', { count: notificationCount });
  useEffect(() => { onSaveSummaryChange?.(saveSummary); }, [onSaveSummaryChange, saveSummary]);
  function updateDraft(next: DrawAssignment[]) { onChange(next); setAssignments(next); }
  function recordUsage(action: 'manual_assignment' | 'redraw' | 'auto_fill') {
    if (premium) onPremiumUsage?.(`${existingDraw ? 'existing' : 'initial'}_${action}`);
  }
  function clearAll() {
    if (disabled) return;
    if (!incomplete) setCompletion(assignments);
    setSelecting(null);
    recordUsage('manual_assignment');
    updateDraft(cards.map(card => ({ id: card.id, wardId: 0 })));
    setManual(true);
    setClearingSelections(true);
    setSingleRepairActive(false);
    setRevealed(new Set(cards.map(card => card.id)));
  }
  function fillRemaining() {
    if (disabled) return;
    const missing = assignments.filter(pair => !pair.wardId);
    if (existingDraw && missing.length === 1 && (freeRepair || singleRepairActive) && !clearingSelections) {
      const giver = missing[0].id;
      const candidates = cards.map(card => ({ recipient: card.id, next: insertPreviewParticipant(cards, assignments, giver, card.id) })).filter(candidate => candidate.next);
      const chosen = candidates[Math.floor(Math.random() * candidates.length)];
      if (!chosen?.next) { showToast(t('game.draw.manual.cannotFill')); return; }
      updateDraft(chosen.next);
      setCompletion(chosen.next);
      setSelecting(null);
      setManual(false);
      onRepairChange?.({ id: giver, wardId: chosen.recipient });
      const changedIds = new Set(chosen.next.filter(pair => assignments.find(previous => previous.id === pair.id)?.wardId !== pair.wardId).map(pair => pair.id));
      setRevealed(previous => new Set([...previous].filter(id => !changedIds.has(id))));
      return;
    }
    const newcomerIds = new Set(initial.filter(pair => !pair.wardId).map(pair => pair.id));
    const newcomerOnly = existingDraw && newcomerIds.size > 1 && !clearingSelections && assignments.every(pair =>
      newcomerIds.has(pair.id) ? !pair.wardId || newcomerIds.has(pair.wardId) : pair.wardId === originalRecipients.get(pair.id));
    let next: DrawAssignment[] | null;
    if (newcomerOnly) {
      const filled = completeManualDraw(cards.filter(card => newcomerIds.has(card.id)), assignments.filter(pair => newcomerIds.has(pair.id)), false, true);
      const recipients = new Map(filled?.map(pair => [pair.id, pair.wardId]));
      next = filled ? assignments.map(pair => ({ ...pair, wardId: recipients.get(pair.id) || pair.wardId })) : null;
    } else {
      next = completeManualDraw(cards, assignments, circleDraw, true)
        || (completion.every(pair => pair.wardId > 0) && assignments.every(pair => !pair.wardId || completion.some(item => item.id === pair.id && item.wardId === pair.wardId)) ? completion : null);
    }
    if (!next) { showToast(t('game.draw.manual.cannotFill')); return; }
    if (!newcomerOnly) recordUsage('auto_fill');
    updateDraft(next);
    setCompletion(next);
    setSelecting(null);
    setManual(false);
    setClearingSelections(false);
    setSingleRepairActive(false);
    setRevealed(new Set([...revealed].filter(id => assignments.some(pair => pair.id === id && pair.wardId > 0))));
  }
  const assignmentById = new Map(assignments.map(pair => [pair.id, pair.wardId]));
  const rows = cards.map(card => ({ id: card.id, wardId: canView ? assignmentById.get(card.id) || 0 : 0 }));
  // Keep the initial missing participants first throughout this editing session.
  if (existingDraw) rows.sort((a, b) => Number(Boolean(originalRecipients.get(a.id))) - Number(Boolean(originalRecipients.get(b.id))));
  const people = new Map(cards.map((card) => [card.id, card]));
  const isNewcomerInsertion = (giverId: number) => freeRepair || (premium && existingDraw && singleRepairActive && !clearingSelections
    && initial.filter(pair => !pair.wardId).length === 1 && !originalRecipients.get(giverId));
  function unavailableReason(giver: CardInfo, recipient: CardInfo, currentRecipient: number): string | undefined {
    if (giver.id === recipient.id) return t('game.draw.manual.selfGift');
    if ((giver.exclusions || []).includes(recipient.id)) return t('game.draw.manual.excluded');
    const colors = (card: CardInfo) => (card.drawColors || []).filter(color => color && !['#FFF', '#FFFFFF'].includes(color.toUpperCase()));
    if (colors(giver).some(color => colors(recipient).includes(color))) return t('game.draw.manual.sameGroup');
    if (isNewcomerInsertion(giver.id)) return insertPreviewParticipant(cards, initial, giver.id, recipient.id) ? undefined : t('game.draw.manual.invalidReplacement');
    if (recipient.id === currentRecipient) return undefined;
    if (manual) {
      if (assignments.some(pair => pair.id !== giver.id && pair.wardId === recipient.id)) return t('game.draw.manual.alreadyAssigned');
      if (manualChoice(giver.id, recipient.id)) return undefined;
      const partial = assignments.map(pair => pair.id === giver.id ? { ...pair, wardId: recipient.id } : pair);
      if (circleDraw && completeManualDraw(cards, partial, false)) return t('game.draw.manual.breaksCircle');
      return t('game.draw.manual.noCompletion');
    }
    if (changePreviewGiftee(cards, assignments, giver.id, recipient.id, circleDraw)) return undefined;
    return t(circleDraw ? 'game.draw.manual.circleReplacement' : 'game.draw.manual.invalidReplacement');
  }
  function redraw() {
    if (disabled || !premium) return;
    try {
      const next = previewBoxDraw(cards.map(card => ({ ...card, wardId: 0 })), circleDraw);
      recordUsage('redraw');
      onChange(next);
      setAssignments(next);
      setRevealed(new Set());
      setManual(false);
      setClearingSelections(false);
      setSingleRepairActive(false);
      setSelecting(null);
      setAnimatingShuffle(true);
      setShuffleVersion(version => version + 1);
    } catch {
      showToast(t('game.draw.failed'));
    }
  }
  function reveal(id: number) {
    setRevealed((previous) => {
      const next = new Set(previous);
      next.add(id);
      return next;
    });
  }
  const toolActions = (
    <div className="draw-preview__tool-actions">
      <Button variant="secondary" size="small" icon={!premium ? <LockIcon /> : undefined} disabled={disabled || !premium} onClick={clearAll} testId="draw-preview-clear">{t('game.draw.manual.clear')}</Button>
      <Button variant="secondary" size="small" icon={premium ? <RefreshIcon /> : <LockIcon />} disabled={disabled || !premium} onClick={redraw} testId="draw-preview-redraw">{t('game.draw.shuffleAgain')}</Button>
      <Button variant="secondary" size="small" icon={!premium ? <LockIcon /> : undefined} disabled={disabled || !premium || !incomplete} onClick={fillRemaining} testId="draw-preview-fill">{t('game.draw.manual.fill')}</Button>
    </div>
  );
  return (
    <Stack gap={4}>
      <div className="draw-preview__intro">
        <Typography as="p" role="body/strong" testId="draw-preview-status" aria-live="polite">
          {t(existingDraw ? 'game.draw.editExisting.ready' : 'game.draw.previewReady', { count: cards.length })}
        </Typography>
        {(!existingDraw || premium || freeRepair || additionalCount > 0) && <Typography as="p" role="body/strong" className="draw-preview__save-notice" testId="draw-preview-save-notice">
          {t(existingDraw ? 'game.draw.saveNotice.existing' : 'game.draw.saveNotice.initial', { action: t(existingDraw ? 'game.draw.editExisting.save' : 'game.draw.confirmResults') })}
        </Typography>}
        {!premium && !existingDraw && <Typography as="p" role="body/base" className="draw-preview__free-review-hint" testId="draw-preview-free-review-hint">
          <Trans i18nKey="game.draw.afterDrawFreeHint" components={{ strong: <strong /> }} />
        </Typography>}
        {!premium && existingDraw && !freeRepair && !additionalCount && <Typography as="p" role="body/base">
          {t('game.draw.editExisting.freeBody')}
        </Typography>}
      </div>
      {canView && <div className="draw-preview__notice" data-testid="draw-preview-surprise">
        <LockIcon />
        <Typography as="p" role="body/base">{t('game.draw.secretWarning')}</Typography>
      </div>}
      {additionalCount > 0 && <Stack gap={3}>
        <Typography as="p" role="body/base" testId="draw-preview-additional-hint">{t('game.draw.additionalHint')}</Typography>
        <Button variant="secondary" icon={<RefreshIcon />} disabled={disabled || additionalReady} testId="draw-preview-additional" onClick={() => {
          try {
            const newPairs = previewBoxDraw(cards.filter(card => !originalRecipients.get(card.id)), false);
            const recipients = new Map(newPairs.map(pair => [pair.id, pair.wardId]));
            updateDraft(initial.map(pair => ({ ...pair, wardId: recipients.get(pair.id) || pair.wardId })));
            setRevealed(new Set());
            setAdditionalReady(true);
            onAdditionalPrepare?.();
          } catch { showToast(t('game.draw.failed')); }
        }}>{t(additionalReady ? 'game.draw.additionalReady' : 'game.draw.runAdditional')}</Button>
      </Stack>}
      {freeRepair && <Stack gap={3}>
        <Typography as="p" role="body/base" testId="draw-preview-repair-hint">{t('game.draw.repairHint')}</Typography>
        <Button variant="secondary" icon={<RefreshIcon />} onClick={fillRemaining} disabled={disabled || !assignments.some(pair => !pair.wardId)} testId="draw-preview-fill">{t('game.draw.manual.fill')}</Button>
      </Stack>}
      {!premium ? (
        <PremiumUpsell feature="draw" completedDraw={existingDraw} compact layout="split" testId="draw-preview-upsell" />
      ) : <div className="draw-preview__tools" data-testid="draw-preview-tools">
        <Typography as="p" role="body/strong" className="draw-preview__tools-title"><PremiumFeatureBadge feature="draw" /><span>{t('game.draw.manual.title')}</span></Typography>
        <Typography as="p" role="body/base">{t('game.draw.manual.body')}</Typography>
        {toolActions}
        {premium && existingDraw && initial.filter(pair => !pair.wardId).length > 1 && incomplete && !clearingSelections && <Typography as="p" role="body/strong" testId="draw-preview-fill-newcomers-hint">{t('game.draw.fillNewcomersHint')}</Typography>}
        {manual && clearingSelections && <Typography as="p" role="body/base" aria-live="polite" testId="draw-preview-progress">{t('game.draw.manual.progress', { count: assignments.filter(pair => pair.wardId).length, total: cards.length })} {t('game.draw.manual.selectionHint')}</Typography>}
      </div>}
      <div className={`draw-preview__pairs${canView ? ' draw-preview__pairs--review' : ''}`} data-testid="draw-preview-pairs">
        <table>
          <colgroup>
            <col className="draw-preview__giver-column" />
            {canView && <col className="draw-preview__relation-column" />}
            <col className="draw-preview__receiver-column" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">{t('game.draw.giver')}</th>
              {canView && <th scope="col" aria-label={t('game.draw.editPair')} />}
              <th scope="col">{t('game.draw.receiver')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((pair, index) => {
              const santa = people.get(pair.id)!;
              const visible = canView && revealed.has(pair.id);
              const giftee = visible ? people.get(pair.wardId)! : null;
              const editControl = <Button variant="blue" size="small" icon={<CrownIcon className="draw-preview__edit-crown" />} onClick={() => openPremium('draw', undefined, 'edit_draw_pair', 'feature')} testId="draw-preview-edit-premium" className="draw-preview__premium-edit">{t('game.draw.editPair')}</Button>;
              const recipientSelector = (premium || (freeRepair && !originalRecipients.get(pair.id))) && visible && (
                      <div className="draw-preview__edit">
                        <Select
                          aria-label={t('game.draw.chooseGiftee')}
                          placeholder={t('game.draw.chooseGiftee')}
                          selectedLabel={giftee ? <span className="draw-preview__selected-person"><ParticipantFace card={giftee} index={cards.findIndex(card => card.id === giftee.id)} /><span>{giftee.username}</span></span> : undefined}
                          value={String(pair.wardId)}
                          disabled={disabled}
                          open={selecting === pair.id}
                          onOpenChange={open => setSelecting(open ? pair.id : null)}
                          options={cards.filter(card => card.id !== pair.id).map(card => {
                            const reason = selecting === pair.id ? unavailableReason(santa, card, pair.wardId) : undefined;
                            return { value: String(card.id), label: card.username, description: reason, disabled: Boolean(reason) };
                          })}
                          onValueChange={(value) => {
                            if (isNewcomerInsertion(pair.id)) {
                              const next = insertPreviewParticipant(cards, initial, pair.id, Number(value));
                              if (!next) return;
                              updateDraft(next);
                              setCompletion(next);
                              if (premium) setManual(false);
                              onRepairChange?.({ id: pair.id, wardId: Number(value) });
                              setRevealed(new Set([...initial.filter(item => !item.wardId).map(item => item.id), ...next.filter(item => initial.find(old => old.id === item.id)?.wardId !== item.wardId).map(item => item.id)]));
                              return;
                            }
                            setSingleRepairActive(false);
                            if (manual) {
                              const possible = manualChoice(pair.id, Number(value));
                              if (!possible) return;
                              setCompletion(possible);
                            }
                            const next = manual ? assignments.map(item => item.id === pair.id ? { ...item, wardId: Number(value) } : item) : changePreviewGiftee(
                              cards,
                              assignments,
                              pair.id,
                              Number(value),
                              circleDraw,
                            );
                            if (!next) return;
                            if (Number(value) !== pair.wardId) recordUsage('manual_assignment');
                            onChange(next);
                            setAssignments(next);
                            setRevealed(
                              new Set([
                                ...revealed,
                                ...next
                                  .filter(
                                    (item, index) => item.wardId !== assignments[index].wardId,
                                  )
                                  .map((item) => item.id),
                              ]),
                            );
                          }}
                          testId="draw-preview-select"
                        />
                      </div>
                    );
              return (
                <tr
                  key={pair.id}
                  data-draft-changed={Boolean(assignmentById.get(pair.id)) && assignmentById.get(pair.id) !== (originalRecipients.get(pair.id) || 0) || undefined}
                  data-giver-id={pair.id}
                  data-receiver-id={visible ? pair.wardId : undefined}
                >
                  <td>
                    <div className="draw-preview__person">
                      <ParticipantFace
                        card={santa}
                        index={index}
                      />
                      <span>{santa.username}</span>
                    </div>
                  </td>
                  {canView && !recipientSelector && <td className="draw-preview__relation-action"><ArrowRightIcon aria-hidden="true" />{giftee && !freeRepair && editControl}</td>}
                  <td className="draw-preview__giftee-cell" colSpan={recipientSelector ? 2 : 1}>
                    {!freeRepair && visible && (clearingSelections ? Boolean(pair.wardId) : additionalReady || originalRecipients.get(pair.id) === pair.wardId) && <button
                      type="button"
                      className="draw-preview__hide"
                      aria-label={t(clearingSelections ? 'game.draw.manual.clearPerson' : 'game.draw.hidePerson', { name: santa.username })}
                      data-testid="draw-preview-hide"
                      disabled={disabled}
                      onClick={() => {
                        setSelecting(null);
                        if (clearingSelections) updateDraft(assignments.map(item => item.id === pair.id ? { ...item, wardId: 0 } : item));
                        else setRevealed(previous => { const next = new Set(previous); next.delete(pair.id); return next; });
                      }}
                    ><CloseIcon /></button>}
                    {recipientSelector || (!canView ? (
                      <div className="draw-preview__locked">
                        <LockIcon />
                        <span>{t('game.draw.premiumPreview')}</span>
                      </div>
                    ) : !pair.wardId ? (premium && existingDraw && initial.filter(item => !item.wardId).length > 1 && !clearingSelections ? <Button variant="secondary" size="small" onClick={fillRemaining} disabled={disabled} testId="draw-preview-fill-row" className="draw-preview__fill-row">{t('game.draw.fillRow')}</Button> : <Typography as="p" role="body/strong">{t('game.draw.unassigned')}</Typography>) : giftee ? (
                      <div
                        className="draw-preview__recipient draw-preview__recipient--revealed"
                        aria-live="polite"
                      >
                        <ParticipantFace
                          card={giftee}
                          index={cards.findIndex((card) => card.id === giftee.id)}
                        />
                        <span>{giftee.username}</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="draw-preview__recipient"
                        disabled={disabled}
                        onClick={() => reveal(pair.id)}
                        aria-label={t('game.draw.revealPerson', { name: santa.username })}
                        data-testid="draw-preview-reveal"
                      >
                        <span key={shuffleVersion} className={`draw-preview__question${animatingShuffle ? ' draw-preview__question--shuffle' : ''}`} aria-hidden="true">
                          ?
                        </span>
                        <span>{t('game.draw.revealOne')}</span>
                      </button>
                    ))}

                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Stack>
  );
}
